import React, { useCallback, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { QRCodeSVG } from 'qrcode.react'
import { intl } from 'lang/intl'

import { TtButton, TtIcon, TtSkeleton } from '@23/ui-react'

import { ENV_SETTINGS } from '../../../global/envSettings'
import { useModal } from '../../../Layout/Modal'
import { useCamCodeQuery } from '../../hooks/useCamCode'

import './SecondCameraModal.scss'

const SECOND_CAM_URL =
  ENV_SETTINGS.environment === 'production'
    ? 'https://login.twentythree.com/camera'
    : 'https://login.dev.twentythree.com/camera'
const QR_SIZE = 160

const SecondCamCodeIntro = () => (
  <p>
    <FormattedMessage defaultMessage="Scan this QR code with your mobile device's camera to use it as an additional camera on your webinar." />
  </p>
)

const SecondCamCodeFooter = () => (
  <>
    {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
    <div className="link">https://twentythree.com/cam</div>
    <small>
      <FormattedMessage defaultMessage="The code will expire in 10 minutes" />
    </small>
  </>
)

const SecondCamCodeError = ({ onRetry }: { onRetry: () => void }) => (
  <div className="join-content join-error">
    <TtIcon className="join-error-icon" library="one" name="page-error" />
    <p>
      <FormattedMessage defaultMessage="Failed to load the camera code." />
    </p>
    <TtButton
      size="small"
      variant="default"
      color="secondary"
      onClick={onRetry}
    >
      <FormattedMessage defaultMessage="Retry" />
    </TtButton>
  </div>
)

const SecondCamCodeLoading = () => (
  <>
    <SecondCamCodeIntro />
    <div className="join-content">
      <TtSkeleton className="join-qr-skeleton" />
    </div>
    <SecondCamCodeFooter />
  </>
)

const SecondCamQrCode = ({ code }: { code: string }) => (
  <div className="join-qr-container">
    <QRCodeSVG
      className="join-qr"
      value={`${SECOND_CAM_URL}?code=${code}`}
      size={QR_SIZE}
      level="L"
      aria-label={intl.formatMessage({
        defaultMessage: 'Second camera join link QR code',
      })}
    />
  </div>
)

const SecondCamCodeDisplay = ({ code }: { code: string }) => {
  const [isQrCode, setIsQrCode] = useState(true)
  const toggleButtonRef = useRef<React.ComponentRef<typeof TtButton>>(null)

  const toggleDisplayState = () => {
    setIsQrCode((value) => !value)
    // Drop focus so the reveal/blur state doesn't stay stuck after clicking.
    void toggleButtonRef.current?.removeFocus()
  }

  return (
    <>
      <SecondCamCodeIntro />
      <div className="join-content">
        {isQrCode ? (
          <SecondCamQrCode code={code} />
        ) : (
          <div className="join-code">{code}</div>
        )}
        <TtButton
          ref={toggleButtonRef}
          className="join-toggle"
          size="small"
          variant="default"
          color="secondary"
          onClick={toggleDisplayState}
        >
          {isQrCode ? (
            <FormattedMessage defaultMessage="Show digits" />
          ) : (
            <FormattedMessage defaultMessage="Show QR code" />
          )}
        </TtButton>
      </div>
      <SecondCamCodeFooter />
    </>
  )
}

const SecondCamCode = () => {
  const { isLoading, isFetching, data, isError, refetch } = useCamCodeQuery()

  const isRetrying = isError && isFetching

  if (isLoading || isRetrying) {
    return <SecondCamCodeLoading />
  }

  if (isError || !data) {
    return <SecondCamCodeError onRetry={() => void refetch()} />
  }

  return <SecondCamCodeDisplay code={data} />
}

const useOpenSecondCamCode = () => {
  const { openModal } = useModal()

  return useCallback(() => {
    openModal({
      label: intl.formatMessage({ defaultMessage: 'Add a 2ndCam' }),
      body: <SecondCamCode />,
      closable: true,
      className: 'second-cam-code-container',
    })
  }, [openModal])
}

export { useOpenSecondCamCode }
